import React, { useState, useEffect } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
// import { Switch } from '@/components/ui/switch';
import { 
  Plus, 
  Edit, 
  Trash2, 
  Tag as TagIcon, 
  Calendar, 
  Clock, 
  Target,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import { Client } from '@/types/client';
import { Tag, NewTag, TagFormData, TagStats, TAG_TYPES, getTagTypeDescription, canHaveDaysConfig } from '@/types/tag';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import api from '@/lib/api';

interface TagsTabProps {
  client: Client | null;
}

const TagsTab: React.FC<TagsTabProps> = ({ client }) => {
  const [tags, setTags] = useState<Tag[]>([]);
  const [stats, setStats] = useState<TagStats[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTag, setEditingTag] = useState<Tag | null>(null);
  const [filterType, setFilterType] = useState<string>('all');
  const [filterActive, setFilterActive] = useState<string>('all');
  
  // Estado do formulário
  const [formData, setFormData] = useState<TagFormData>({
    name: '',
    type: 'LEMBRETE',
    days_before_due: '',
    days_after_due: '',
    is_active: true,
    description: ''
  });
  
  // Estado para controlar qual tag está sendo editada inline
  const [editingTagId, setEditingTagId] = useState<number | null>(null);

  // Carregar tags do cliente
  useEffect(() => {
    if (client?.id) {
      loadTags();
      loadStats();
    }
  }, [client?.id]);

  const loadTags = async () => {
    if (!client?.id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filterType !== 'all') params.append('type', filterType);
      if (filterActive !== 'all') params.append('is_active', filterActive);

      const result = await api.get(`/tags/client/${client.id}?${params.toString()}`);
      setTags(result.data || []);
    } catch (error) {
      console.error('Erro ao carregar tags:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar tags",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    if (!client?.id) return;

    try {
      const result = await api.get(`/tags/client/${client.id}/stats`);
      setStats(result.data || []);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    }
  };

  // Recarregar quando filtros mudarem
  useEffect(() => {
    loadTags();
  }, [filterType, filterActive]);

  const resetForm = () => {
    setFormData({
      name: '',
      type: 'LEMBRETE',
      days_before_due: '',
      days_after_due: '',
      is_active: true,
      description: ''
    });
    setEditingTag(null);
    setShowForm(false);
    setEditingTagId(null);
  };

  const handleEdit = (tag: Tag) => {
    // Se já está editando esta tag, fechar o formulário
    if (editingTagId === tag.id) {
      setEditingTagId(null);
      setEditingTag(null);
      return;
    }
    
    // Abrir formulário inline para esta tag
    setFormData({
      name: tag.name,
      type: tag.type,
      days_before_due: tag.days_before_due?.toString() || '',
      days_after_due: tag.days_after_due?.toString() || '',
      is_active: tag.is_active,
      description: tag.description || ''
    });
    setEditingTag(tag);
    setEditingTagId(tag.id);
    setShowForm(false); // Não mostrar o formulário principal
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!client?.id) return;

    // Validações
    if (!formData.name.trim()) {
      toast({
        title: "Erro",
        description: "Nome da tag é obrigatório",
        variant: "destructive"
      });
      return;
    }

    if (formData.name.length > 20) {
      toast({
        title: "Erro",
        description: "Nome da tag deve ter no máximo 20 caracteres",
        variant: "destructive"
      });
      return;
    }

    // Validação de dias removida - agora configurada apenas na estratégia

    setSaving(true);
    try {
      const tagData: NewTag = {
        name: formData.name.trim(),
        type: formData.type,
        is_active: formData.is_active,
        description: formData.description.trim() || undefined
      };

      // Configuração de dias removida - agora configurada apenas na estratégia

      if (editingTag) {
        // Atualizar tag existente
        await api.put(`/tags/${editingTag.id}`, tagData);
        toast({
          title: "Sucesso",
          description: "Tag atualizada com sucesso"
        });
      } else {
        // Criar nova tag
        await api.post(`/tags/client/${client.id}`, tagData);
        toast({
          title: "Sucesso",
          description: "Tag criada com sucesso"
        });
      }

      resetForm();
      loadTags();
      loadStats();
    } catch (error: any) {
      console.error('Erro ao salvar tag:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao salvar tag';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (tag: Tag) => {
    if (!confirm(`Tem certeza que deseja deletar a tag "${tag.name}"?`)) {
      return;
    }

    try {
      await api.delete(`/tags/${tag.id}`);
      toast({
        title: "Sucesso",
        description: "Tag deletada com sucesso"
      });
      loadTags();
      loadStats();
    } catch (error: any) {
      console.error('Erro ao deletar tag:', error);
      const errorMessage = error.response?.data?.error || 'Erro ao deletar tag';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    }
  };

  const getTypeColor = (type: string) => {
    const colors = {
      'LEMBRETE': 'bg-blue-100 text-blue-800',
      'COBRANCA': 'bg-red-100 text-red-800',
      'REATIVACAO': 'bg-green-100 text-green-800',
      'MARKETING': 'bg-purple-100 text-purple-800',
      'REVISTORIA': 'bg-orange-100 text-orange-800',
      'PAGAMENTO': 'bg-emerald-100 text-emerald-800',
      'ANIVERSARIO': 'bg-rose-100 text-rose-800',
      'RENOVACAO': 'bg-cyan-100 text-cyan-800'
    };
    return colors[type as keyof typeof colors] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <TabsContent value="tags" className="space-y-4 mt-0">
        <AtomicLoadingScreen 
          message="Carregando tags..." 
          className="h-64"
        />
      </TabsContent>
    );
  }

  return (
    <TabsContent value="tags" className="space-y-6 mt-0">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 flex items-center gap-2">
            <TagIcon className="w-5 h-5 text-blue-600" />
            Tags Personalizadas
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            Configure tags personalizadas para diferentes tipos de envio
          </p>
        </div>
        <Button 
          onClick={() => setShowForm(true)}
          className="bg-blue-600 hover:bg-blue-700"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nova Tag
        </Button>
      </div>

      {/* Estatísticas */}
      {stats.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {stats.map((stat) => (
            <Card key={stat.type} className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {getTagTypeDescription(stat.type as any)}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">
                    {stat.count}
                  </p>
                  <p className="text-xs text-green-600">
                    {stat.active_count} ativas
                  </p>
                </div>
                <div className={`p-2 rounded-lg ${getTypeColor(stat.type)}`}>
                  <TagIcon className="w-4 h-4" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Filtros */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Filtros</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="filter-type">Tipo</Label>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os tipos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  {TAG_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="filter-active">Status</Label>
              <Select value={filterActive} onValueChange={setFilterActive}>
                <SelectTrigger>
                  <SelectValue placeholder="Todos os status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="true">Ativas</SelectItem>
                  <SelectItem value="false">Inativas</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Formulário */}
      {showForm && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">
              {editingTag ? 'Editar Tag' : 'Nova Tag'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Nome da Tag *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Ex: Lembrete 5 dias"
                    maxLength={20}
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.name.length}/20 caracteres
                  </p>
                </div>
                <div>
                  <Label htmlFor="type">Tipo *</Label>
                  <Select 
                    value={formData.type} 
                    onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TAG_TYPES.map((type) => (
                        <SelectItem key={type.value} value={type.value}>
                          <div>
                            <div className="font-medium">{type.label}</div>
                            <div className="text-xs text-gray-500">{type.description}</div>
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Campos de dias removidos - configuração agora apenas na estratégia */}

              <div>
                <Label htmlFor="description">Descrição</Label>
                <Textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Descrição opcional da tag"
                  rows={3}
                />
              </div>

              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                />
                <Label htmlFor="is_active">Tag ativa</Label>
              </div>

              <div className="flex gap-2 pt-4">
                <Button type="submit" disabled={saving}>
                  {saving ? 'Salvando...' : (editingTag ? 'Atualizar' : 'Criar')}
                </Button>
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancelar
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      {/* Lista de Tags */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Tags Configuradas ({tags.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {tags.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <TagIcon className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>Nenhuma tag encontrada</p>
              <p className="text-sm">Crie sua primeira tag para começar</p>
            </div>
          ) : (
            <div className="space-y-3">
              {tags.map((tag) => (
                <div key={tag.id} className="border rounded-lg">
                  <div className="flex items-center justify-between p-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="font-medium">{tag.name}</span>
                        <Badge className={getTypeColor(tag.type)}>
                          {getTagTypeDescription(tag.type)}
                        </Badge>
                        {tag.is_active ? (
                          <CheckCircle className="w-4 h-4 text-green-500" />
                        ) : (
                          <AlertCircle className="w-4 h-4 text-gray-400" />
                        )}
                      </div>
                      
                      {tag.description && (
                        <p className="text-sm text-gray-600 mb-2">{tag.description}</p>
                      )}
                      
                      {/* Informações de dias removidas - agora configuradas apenas na estratégia */}
                    </div>
                    
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(tag)}
                        className={editingTagId === tag.id ? "bg-blue-50 border-blue-200" : ""}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(tag)}
                        className="text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                  
                  {/* Formulário de edição inline */}
                  {editingTagId === tag.id && (
                    <div className="border-t bg-gray-50 p-4">
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <Label htmlFor={`name-${tag.id}`}>Nome da Tag *</Label>
                            <Input
                              id={`name-${tag.id}`}
                              value={formData.name}
                              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                              placeholder="Ex: Lembrete 5 dias"
                              maxLength={20}
                              required
                            />
                            <p className="text-xs text-gray-500 mt-1">
                              {formData.name.length}/20 caracteres
                            </p>
                          </div>
                          <div>
                            <Label htmlFor={`type-${tag.id}`}>Tipo *</Label>
                            <Select 
                              value={formData.type} 
                              onValueChange={(value: any) => setFormData({ ...formData, type: value })}
                            >
                              <SelectTrigger>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {TAG_TYPES.map((type) => (
                                  <SelectItem key={type.value} value={type.value}>
                                    <div>
                                      <div className="font-medium">{type.label}</div>
                                      <div className="text-xs text-gray-500">{type.description}</div>
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>

                        {/* Campos de dias removidos - configuração agora apenas na estratégia */}

                        <div>
                          <Label htmlFor={`description-${tag.id}`}>Descrição</Label>
                          <Textarea
                            id={`description-${tag.id}`}
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            placeholder="Descrição opcional da tag"
                            rows={3}
                          />
                        </div>

                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id={`is_active-${tag.id}`}
                            checked={formData.is_active}
                            onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                            className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500"
                          />
                          <Label htmlFor={`is_active-${tag.id}`}>Tag ativa</Label>
                        </div>

                        <div className="flex gap-2 pt-2">
                          <Button type="submit" disabled={saving} size="sm">
                            {saving ? 'Salvando...' : 'Atualizar'}
                          </Button>
                          <Button 
                            type="button" 
                            variant="outline" 
                            size="sm"
                            onClick={() => setEditingTagId(null)}
                          >
                            Cancelar
                          </Button>
                        </div>
                      </form>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </TabsContent>
  );
};

export default TagsTab;
